import { useState, useEffect, useCallback } from 'react';
import { AxiosError } from 'axios';
import { OrderStatus } from '../../utils/orderUtils';
import { OrderItem } from '../../types/order';
import apiClient from '../../config/axiosConfig';

export interface OrderDetail {
  id: number;
  user_id: number;
  user_name: string;
  user_surname: string;
  management: string;
  office: string;
  total_amount: number;
  status: OrderStatus;
  ticket_used: number;
  created_at: string;
  updated_at: string;
  items: OrderItem[];
}

interface UseOrderDetailProps {
  orderId: string | undefined;
}

interface UseOrderDetailReturn {
  order: OrderDetail | null;
  loading: boolean;
  error: string | null;
  updating: boolean;
  pendingStatus: OrderStatus | null;
  updateOrderStatus: (newStatus: OrderStatus) => Promise<void>;
  confirmStatusUpdate: () => Promise<void>;
  cancelStatusUpdate: () => void;
}

export const useOrderDetail = ({ orderId }: UseOrderDetailProps): UseOrderDetailReturn => {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<OrderStatus | null>(null);

  const fetchOrder = useCallback(async () => {
    if (!orderId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(`/api/orders/${orderId}`);
      setOrder(response.data);
    } catch (err) {
      const error = err as AxiosError;
      const message = (error.response?.data as any)?.message || error.message || 'Bir hata oluştu';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const updateOrderStatus = async (newStatus: OrderStatus) => {
    if (!order || !orderId) {
      return;
    }

    // İlk tıklamada pending duruma geç (inline onay için)
    setPendingStatus(newStatus);
  };

  const confirmStatusUpdate = async () => {
    if (!order || !orderId || !pendingStatus) {
      return;
    }

    try {
      setUpdating(true);

      const response = await apiClient.patch(`/api/orders/${orderId}/status`, {
        status: pendingStatus,
      });

      setOrder(response.data);
      setPendingStatus(null);
    } catch (err) {
      const error = err as AxiosError;
      const message = (error.response?.data as any)?.message || error.message || 'Bir hata oluştu';
      alert(message);
    } finally {
      setUpdating(false);
    }
  };

  const cancelStatusUpdate = () => {
    setPendingStatus(null);
  };

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  return {
    order,
    loading,
    error,
    updating,
    pendingStatus,
    updateOrderStatus,
    confirmStatusUpdate,
    cancelStatusUpdate,
  };
};
